import {
  AfterViewChecked,
  Component,
  ViewEncapsulation,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Subject, of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  startWith,
  switchMap,
  takeUntil,
} from 'rxjs/operators';
import { SearchService } from 'src/app/shared/services/metadata-api/search.service';
import { QueryParamsService } from 'src/app/shared/utils/query-params.service';
import { MatLegacyAutocomplete } from '@angular/material/legacy-autocomplete';
import { QueryFilter } from 'src/app/shared/models/ai-search.interface';

// The fields a host is willing to accept. DD omits events on purpose: its tiles
// measure the event spread of the matched corpus, so filtering by event would
// make the page measure its own filter.
export type TermField = 'text' | 'entity' | 'event' | 'source';

// The pinned action. Carried as an option value so it is keyboard-reachable
// like every other row, rather than a click-only affordance.
export interface FreeTextOption {
  freeText: string;
}

export interface TermPickerOption {
  field: string;
  value: string; // what the query matches on — a compound key for typed fields
  name: string; // what the user reads
  group: string; // section heading
  meta?: string; // matched form / source url
  country?: string;
  count?: number;
}

const MIN_QUERY = 2;

/** Artifact lists are the user's own and can be long; a picker is not a browser. */
const MAX_ARTIFACTS = 6;

/** While browsing, how many of each kind to show — see artifactMatches. */
const BROWSE_PER_KIND = 4;

/** A stable reference, so re-attaching on each open never registers twice. */
const keepFocus = (event: Event): void => event.preventDefault();

@Component({
  // The panel renders in a CDK overlay outside this component, so its styles
  // have to be global — same reason company-form does it.
  encapsulation: ViewEncapsulation.None,
  selector: 'app-term-picker',
  templateUrl: './term-picker.component.html',
  styleUrls: ['./term-picker.component.scss'],
})
export class TermPickerComponent
  implements AfterViewChecked, OnChanges, OnInit, OnDestroy
{
  /**
   * The overlay pane is otherwise sized to the trigger, and this one is a pill
   * input — which left the name column a few characters wide.
   */
  readonly PANEL_WIDTH = 320;

  /** Fields this host accepts. Free text is always offered. */
  @Input() fields: TermField[] = ['text'];
  /**
   * Saved queries and portfolios this host lets the user embed. Supplied rather
   * than fetched: they are the host's own artifacts, there is no search endpoint
   * for them, and a shared micro-component has no business knowing the customer
   * API. Matched locally against what is typed.
   */
  @Input() artifacts: TermPickerOption[] = [];
  @Input() placeholder = 'Add a term…';
  /** Shown while the artifact list is being browsed — see browsingArtifacts. */
  @Input() browseNote =
    'Your saved queries and portfolios — or type to search companies, events and sources.';
  /**
   * Extra class for the overlay panel. The panel is attached to <body>, outside
   * the page component that declares the palette, so the host has to name it.
   */
  @Input() panelClass = '';

  @Output() pick = new EventEmitter<QueryFilter>();
  @Output() dismiss = new EventEmitter<void>();

  @ViewChild('input', { static: true }) inputRef: ElementRef<HTMLInputElement>;
  @ViewChild('auto', { static: true }) auto: MatLegacyAutocomplete;

  readonly control = new UntypedFormControl('');
  options: TermPickerOption[] = [];
  /**
   * Section headings, in the order the options arrived.
   *
   * A field, not a getter: a getter returns a new array every change-detection
   * pass, and `*ngFor` then destroys and recreates every option — so a result
   * pressed with the mouse was detached before the click could land, and
   * nothing could be selected.
   */
  groups: { name: string; options: TermPickerOption[] }[] = [];
  loading = false;
  /** Set when a lookup fails: free text still works, so this never blocks. */
  failed = false;

  private term$ = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private searchService: SearchService,
    private queryParams: QueryParamsService,
  ) {}

  /**
   * The panel is a CDK overlay outside this component, so it carries its palette
   * and its layout as classes. `--lead` tells the group headings to stick below
   * the free-text action rather than at the top of the panel.
   */
  get panelClasses(): string {
    return `tp-panel ${this.canCommitFreeText ? 'tp-panel--lead ' : ''}${this.panelClass}`;
  }

  /** The typed text. Selecting a result puts an object on the control. */
  get draft(): string {
    const value = this.control.value;
    return typeof value === 'string' ? value : '';
  }

  ngOnChanges(changes: SimpleChanges): void {
    // A host loads its artifacts asynchronously, so the browse list has to
    // appear when they arrive — not only once something is typed.
    if (changes['artifacts'] && !this.draft.trim()) {
      this.setOptions(this.artifactMatches(''));
    }
  }

  ngOnInit(): void {
    this.control.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        if (typeof value === 'string') this.term$.next(value);
      });

    this.term$
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        startWith(''),
        switchMap((value) => {
          const term = value.trim();
          if (!this.wantsLookup || term.length < MIN_QUERY) {
            this.loading = false;
            return of({ term, res: null });
          }
          this.loading = true;
          this.failed = false;
          return this.searchService
            .searchMeta(term, this.queryParams.queryParams ?? {})
            .pipe(
              catchError(() => {
                this.failed = true;
                return of(null);
              }),
              // The term travels with its own result: artifacts are matched
              // against it here, where a late response cannot outrun it.
              map((res) => ({ term, res })),
            );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(({ term, res }) => {
        this.loading = false;
        this.setOptions([
          ...this.artifactMatches(term),
          ...(res ? this.toOptions(res) : []),
        ]);
      });

    setTimeout(() => this.inputRef?.nativeElement?.focus());
  }

  /**
   * Material does not prevent default on a mousedown inside its panel, so
   * pressing a result blurs the input — and a host that tears the picker down on
   * `dismiss` destroys the option between press and release, which makes results
   * unclickable. One listener on the panel covers every row, the group headings,
   * the padding and the scrollbar.
   *
   * Attached on every check rather than on `(opened)`: the panel opens when the
   * input takes focus, which is before the first options exist, so that event
   * fired once with no panel element and never again — and every artifact click
   * was lost. `keepFocus` is a stable reference, so re-adding it is a no-op.
   */
  ngAfterViewChecked(): void {
    this.auto?.panel?.nativeElement?.addEventListener('mousedown', keepFocus);
  }

  /** Selecting never leaves text behind — the picker is add-and-clear. */
  readonly displayEmpty = (): string => '';

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Clicking genuinely outside discards: a half-typed term should never be
  // added on the way past. A press inside the panel keeps focus (see the
  // template), so it never reaches here mid-click.
  onBlur(): void {
    this.dismiss.emit();
  }

  /**
   * With nothing typed, the host's artifacts are listed as they are: a user
   * cannot guess that a saved query or a portfolio can go in a query by typing
   * its name, so the picker shows them before it is asked.
   */
  private artifactMatches(term: string): TermPickerOption[] {
    const needle = term.trim().toLowerCase();
    if (needle) {
      return this.artifacts
        .filter((a) => a.name.toLowerCase().includes(needle))
        .slice(0, MAX_ARTIFACTS);
    }
    // Browsing: a few of each kind. A flat top-6 showed six saved queries and
    // never a portfolio, which is the half the user is least likely to guess at.
    const byKind = new Map<string, TermPickerOption[]>();
    for (const artifact of this.artifacts) {
      const bucket = byKind.get(artifact.group) ?? [];
      if (bucket.length < BROWSE_PER_KIND) bucket.push(artifact);
      byKind.set(artifact.group, bucket);
    }
    return [...byKind.values()].flat();
  }

  /** Shown above the artifact list while nothing has been typed. */
  get browsingArtifacts(): boolean {
    return !this.draft.trim() && !!this.artifacts.length;
  }

  // Nothing to look up when the host only accepts free text.
  private get wantsLookup(): boolean {
    return this.fields.some((f) => f !== 'text');
  }

  get canCommitFreeText(): boolean {
    return !!this.draft.trim();
  }

  /**
   * The free-text action. Takes the term rather than reading the control:
   * Material writes `displayWith` into the input *before* it emits the
   * selection, so by then the draft is already gone and nothing was added.
   */
  commitFreeText(value = this.draft.trim()): void {
    if (!value) return;
    this.emit({ field: 'text', value, name: value });
  }

  onSelected(option: TermPickerOption | FreeTextOption): void {
    // The action carries the term it was rendered with — see commitFreeText.
    if ('freeText' in option) this.commitFreeText(option.freeText);
    else this.emit(option);
  }

  // Material handles ENTER when a result is highlighted, and marks the event
  // handled. An unhandled ENTER means "take what I typed".
  onEnter(event: Event): void {
    if (event.defaultPrevented) return;
    this.commitFreeText();
    event.preventDefault();
  }

  private emit(option: { field: string; value: string; name: string }): void {
    this.pick.emit({
      field: option.field,
      value: option.value,
      // `name` is what the pill shows; `value` stays the resolved id.
      name: option.name,
      input: option.name,
      negated: false,
    });
    this.control.setValue('', { emitEvent: false });
    // The stream has to see the reset too. Without it `distinctUntilChanged`
    // still holds the picked term, and typing the same term again looks up
    // nothing at all.
    this.term$.next('');
    this.setOptions([]);
  }

  trackByOption(_: number, option: TermPickerOption): string {
    return `${option.field}:${option.value}`;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private toOptions(res: any): TermPickerOption[] {
    const out: TermPickerOption[] = [];

    if (this.fields.includes('entity')) {
      for (const hit of res?.entity_prefix_search?.hits ?? []) {
        const entity = hit?.entity;
        if (!entity?.type || !entity?.alpha_id) continue;
        out.push(this.entityOption(hit));
        for (const child of entity.children ?? []) {
          if (child?.entity?.alpha_id) out.push(this.entityOption(child, true));
        }
      }
    }

    if (this.fields.includes('event')) {
      for (const hit of res?.event_search?.hits ?? []) {
        if (!hit?.id) continue;
        out.push({
          field: hit.type ?? 'event',
          value: hit.id,
          name: hit.name,
          group: 'Event',
        });
      }
    }

    if (this.fields.includes('source')) {
      for (const hit of res?.source_prefix_search?.hits ?? []) {
        if (!hit?.source_url || hit.is_filtered) continue;
        out.push({
          field: 'source',
          value: hit.source_url,
          name: hit.name ?? hit.source_url,
          meta: hit.source_url,
          group: 'Source',
        });
      }
    }

    return out;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private entityOption(hit: any, child = false): TermPickerOption {
    const entity = hit.entity;
    return {
      field: 'entity',
      value: `${entity.type}:${entity.alpha_id}`,
      name: entity.name,
      group: child ? 'Subsidiary' : this.titleCase(entity.type),
      meta: entity.matched_form ?? '',
      country: entity.country?.alpha_id?.toUpperCase() ?? '',
      count: hit.count,
    };
  }

  private titleCase(type: string): string {
    if (!type) return 'Entity';
    return type
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  private setOptions(options: TermPickerOption[]): void {
    this.options = options;
    const byName = new Map<string, TermPickerOption[]>();
    for (const option of options) {
      const bucket = byName.get(option.group);
      if (bucket) bucket.push(option);
      else byName.set(option.group, [option]);
    }
    this.groups = [...byName].map(([name, options]) => ({ name, options }));
  }

  trackByGroup(_: number, group: { name: string }): string {
    return group.name;
  }

  countLabel(count: number | undefined): string {
    if (count == null) return '';
    if (count >= 1000000) return `${Math.round(count / 100000) / 10}M`;
    if (count >= 1000) return `${Math.round(count / 100) / 10}K`;
    return String(count);
  }
}
